import { useContext, useMemo } from 'react';
import { ID } from 'types';
import { LineItemsContext } from '@providers/LineItemsProvider';
import { countDeletedSavedRows, findOriginalLot, isLotPagingLocked, useLotIdErrors } from '@features/buyer-line-items-pagination';

/**
 * Hook that extracts only the data specific to a single lot.
 * This prevents unnecessary re-renders when other lots change.
 * ONLY returns lot-specific data to avoid global state re-renders.
 */
export const useLotData = (lotId: ID) => {
  const { state, dispatch } = useContext(LineItemsContext);
  const { lots, lotsErrors, lotsPagination, changes, event, originalLots, shellRefreshCount } = state;
  const paginationEnabled = Boolean(event?.paginationEnabled);

  // Extract lot-specific data first
  const lot = lots?.find(l => l.id === lotId);
  const lotErrors = lotsErrors?.[lotId];
  const lotIdErrors = useLotIdErrors(lotId);
  const lotChanges = changes?.[lotId];
  const lotPagination = paginationEnabled ? lotsPagination?.[lotId] : undefined;
  const originalLot = paginationEnabled ? findOriginalLot(originalLots, lotId) : undefined;
  const loadedRows = originalLot?.line_items;
  const pagingLocked = useMemo(() => (
    paginationEnabled && isLotPagingLocked({ lotChanges, originalLot })
  ), [lotChanges, originalLot, paginationEnabled]);
  const deletedRowCount = useMemo(() => (
    paginationEnabled ? countDeletedSavedRows(lotChanges) : 0
  ), [lotChanges, paginationEnabled]);
  const lotShellRefreshCount = paginationEnabled ? shellRefreshCount : 0;

  // Create stable references for lot-specific data ONLY - only depend on THIS lot's data
  // NOTE: This will still recalculate when lot object reference changes, even if content is same.
  // This is a known limitation - for deeper optimization, consider implementing deep comparison
  // or restructuring the context to provide stable references.
  const lotData = useMemo(() => {
    return {
      deletedRowCount,
      dispatch,
      hasLotChanges: Object.keys(lotChanges || {}).length > 0,
      hasLotErrors: Object.keys((paginationEnabled ? lotIdErrors : lotErrors) || {}).length > 0,
      isPagingLocked: pagingLocked,
      loadedRows,
      lot,
      lotChanges: lotChanges || {},
      lotErrors: lotErrors || {},
      lotIdErrors,
      lotPagination,
      paginationEnabled,
      shellRefreshCount: lotShellRefreshCount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deletedRowCount,
    loadedRows,
    lot,
    lotErrors,
    lotIdErrors,
    lotChanges,
    lotPagination,
    lotShellRefreshCount,
    pagingLocked,
    paginationEnabled,
  ]); // Only THIS lot's data, dispatch is stable from useReducer

  return lotData;
};

/**
 * Hook for accessing shared/global state from LineItemsProvider.
 * Use this for state that all lots need (event, dispatch, etc.)
 */
export const useSharedLineItemsData = () => {
  const { state, dispatch, hasChanges, request } = useContext(LineItemsContext);

  // Destructure state properties once for DRY
  const {
    event,
    isInProgress,
    isLoading,
    refreshValidation,
    shouldRerenderLineItemData
  } = state;

  return useMemo(() => ({
    dispatch,
    event,
    hasChanges,
    isInProgress,
    isLoading,
    refreshValidation,
    request,
    shouldRerenderLineItemData,
  }), [
    dispatch,
    event,
    hasChanges,
    isInProgress,
    isLoading,
    refreshValidation,
    request,
    shouldRerenderLineItemData,
  ]);
};
